import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PencilLine, X, Upload, Loader2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setSiteTextOverrides, spotKeyFor, originalText, originalAttr, SITE_TEXT_PREVIEW_KEY } from "@/lib/siteText";

const ATTRS = ["placeholder", "title", "aria-label"];
const inSkip = (el) => !!el?.closest?.("[data-site-text-skip]");

function textNodeAt(x, y, el) {
  let node = null;
  if (document.caretRangeFromPoint) node = document.caretRangeFromPoint(x, y)?.startContainer;
  else if (document.caretPositionFromPoint) node = document.caretPositionFromPoint(x, y)?.offsetNode;
  if (node?.nodeType === 3 && node.nodeValue.trim() && el.contains(node)) return node;
  return [...el.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim()) || null;
}

// Count how many places on the current page show the same original text.
function countOnPage(orig) {
  let c = 0;
  const tw = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  for (let n = tw.nextNode(); n; n = tw.nextNode()) if (!inSkip(n.parentElement) && originalText(n).trim() === orig) c++;
  return c;
}

function pickTarget(e) {
  const el = e.target;
  const node = textNodeAt(e.clientX, e.clientY, el);
  if (node) return { node, el: node.parentElement, orig: originalText(node).trim(), current: node.nodeValue.trim() };
  const attr = ATTRS.find((a) => el.getAttribute?.(a));
  if (attr) return { el, attr, orig: originalAttr(el, attr).trim(), current: el.getAttribute(attr).trim() };
  return null;
}

// Admin-only: floating "Edit text" mode — click any text on the live site to rename it (saved as draft).
export default function ClickToEditText() {
  const { user } = useAuth();
  const [on, setOn] = useState(false);
  const [box, setBox] = useState(null);
  const [target, setTarget] = useState(null);
  const prevPreview = useRef(null);
  const { pathname } = useLocation();

  const loadDrafts = useCallback(async () => {
    const d = await api.adminSiteText();
    setSiteTextOverrides(d.draft || []);
  }, []);

  const toggle = async () => {
    if (!on) {
      prevPreview.current = localStorage.getItem(SITE_TEXT_PREVIEW_KEY);
      localStorage.setItem(SITE_TEXT_PREVIEW_KEY, "1");
      loadDrafts().catch(() => toast.error("Could not load site text drafts"));
      toast.message("Edit text is on — click any text to rename it. Press Esc to stop.");
    } else {
      setTarget(null); setBox(null);
      if (prevPreview.current !== "1") {
        localStorage.setItem(SITE_TEXT_PREVIEW_KEY, "0");
        api.siteText().then((r) => setSiteTextOverrides(r.overrides)).catch(() => {});
      }
    }
    setOn(!on);
  };

  useEffect(() => {
    if (!on) return undefined;
    const block = (e) => { if (!inSkip(e.target)) { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation(); } };
    const move = (e) => {
      if (inSkip(e.target) || target) return;
      const r = e.target.getBoundingClientRect?.();
      setBox(r ? { x: r.left, y: r.top, w: r.width, h: r.height } : null);
    };
    const click = (e) => {
      if (inSkip(e.target)) return;
      block(e);
      const t = pickTarget(e);
      if (!t || !t.orig) { toast.error("No editable text here — click directly on a word"); return; }
      setTarget({ ...t, spot: spotKeyFor(t.el), count: t.node ? countOnPage(t.orig) : 1 });
    };
    const key = (e) => { if (e.key === "Escape") { if (target) setTarget(null); else toggle(); } };
    const evs = ["mousedown", "mouseup", "pointerdown", "pointerup", "dblclick", "submit"];
    evs.forEach((n) => document.addEventListener(n, block, true));
    document.addEventListener("click", click, true);
    document.addEventListener("mousemove", move, true);
    document.addEventListener("keydown", key, true);
    return () => {
      evs.forEach((n) => document.removeEventListener(n, block, true));
      document.removeEventListener("click", click, true);
      document.removeEventListener("mousemove", move, true);
      document.removeEventListener("keydown", key, true);
    };
  }); // re-bind each render so handlers see the latest target/toggle

  if (user?.role !== "admin") return null;
  return (
    <>
      {on && <style>{`body.cm-edit-text, body.cm-edit-text * { cursor: text !important; }`}</style>}
      <BodyClass on={on} />
      {on && box && !target && (
        <div className="pointer-events-none fixed z-[9998] rounded border-2 border-dashed border-[#FFC94D] bg-[#FFC94D]/10 transition-[left,top,width,height] duration-75"
          style={{ left: box.x - 2, top: box.y - 2, width: box.w + 4, height: box.h + 4 }} data-testid="click-edit-highlight" />
      )}
      {(on || !pathname.startsWith("/editor")) && <EditToolbar on={on} onToggle={toggle} />}
      {target && <EditTextPanel target={target} onClose={() => setTarget(null)} />}
    </>
  );
}

function BodyClass({ on }) {
  useEffect(() => { document.body.classList.toggle("cm-edit-text", on); return () => document.body.classList.remove("cm-edit-text"); }, [on]);
  return null;
}

function EditToolbar({ on, onToggle }) {
  const [busy, setBusy] = useState(false);
  const publish = async () => {
    const d = await api.adminSiteText().catch(() => null);
    const key = (x) => `${x.s || ""}\u0000${x.o}\u0000${x.n}`;
    const pub = new Set((d?.published || []).map(key));
    const draftKeys = new Set((d?.draft || []).map(key));
    const changes = (d?.draft || []).filter((x) => !pub.has(key(x))).length + (d?.published || []).filter((x) => !draftKeys.has(key(x))).length;
    if (!changes) { toast.message("Nothing new to publish"); return; }
    if (!window.confirm(`Publish ${changes} text change${changes === 1 ? "" : "s"} to the live site?`)) return;
    setBusy(true);
    try { const r = await api.publishSiteText(); toast.success(`Published — ${r.count} custom text${r.count === 1 ? "" : "s"} live`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
    setBusy(false);
  };
  return (
    <div data-site-text-skip="" className="fixed bottom-3 right-3 z-[9999] sm:bottom-5 sm:right-5 flex items-center gap-1.5 rounded-full border border-white/15 bg-[#0A0A0A]/90 p-1 shadow-2xl backdrop-blur-xl" data-testid="click-edit-toolbar">
      <button onClick={onToggle} data-testid="click-edit-toggle"
        className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${on ? "bg-[#FFC94D] text-black" : "text-white hover:bg-white/10"}`}>
        {on ? <X size={14} /> : <PencilLine size={14} />} <span className={on ? "" : "hidden sm:inline"}>{on ? "Stop editing" : "Edit text"}</span>
      </button>
      {on && (
        <button onClick={publish} disabled={busy} data-testid="click-edit-publish" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Publish drafts
        </button>
      )}
    </div>
  );
}

function EditTextPanel({ target, onClose }) {
  const [value, setValue] = useState(target.current);
  const [scope, setScope] = useState(target.count > 1 ? null : "spot");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const save = async (n) => {
    if (!scope) { toast.error("Choose where to change it"); return; }
    setBusy(true);
    try {
      const d = await api.saveSiteTextDraft([{ o: target.orig, n, ...(scope === "spot" ? { s: target.spot } : {}) }]);
      setSiteTextOverrides(d.draft);
      toast.success(n ? "Saved as draft — press Publish drafts to make it live" : "Back to original (draft)");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const opt = (k, label, sub) => (
    <button onClick={() => setScope(k)} data-testid={`click-edit-scope-${k}`}
      className={`flex-1 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${scope === k ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
      <div className="font-bold">{label}</div><div className="mt-0.5 text-[10px] text-[#777]">{sub}</div>
    </button>
  );

  return (
    <div data-site-text-skip="" className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" data-testid="click-edit-panel">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-5 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-base font-black text-white">Rename this text</h3>
            <p className="mt-0.5 truncate text-xs text-[#777]" data-testid="click-edit-original">Original: “{target.orig}”{target.attr ? " (placeholder / tooltip)" : ""}</p>
          </div>
          <button onClick={onClose} data-testid="click-edit-close" className="rounded p-1 text-[#A1A1AA] hover:text-white"><X size={16} /></button>
        </div>
        <textarea ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} rows={2} data-testid="click-edit-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(value.trim()); } }}
          className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/60" />
        <p className="mb-2 mt-3 text-xs font-semibold text-[#A1A1AA]">
          {target.count > 1 ? `This text appears ${target.count} times on this page. Change it:` : "Change it:"}
        </p>
        <div className="flex gap-2">
          {opt("spot", "Only this spot", "just this place")}
          {opt("all", "Everywhere", "every place on the site")}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button onClick={() => save("")} disabled={busy} data-testid="click-edit-reset" className="flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white disabled:opacity-40"><RotateCcw size={12} /> Back to original</button>
          <button onClick={() => save(value.trim())} disabled={busy || !value.trim()} data-testid="click-edit-save"
            className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-2 text-xs font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Save draft
          </button>
        </div>
      </div>
    </div>
  );
}
